import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  DatePicker,
  Flashbar,
  FormField,
  Grid,
  Pagination,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  Table,
  TimeInput } from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import Header from '@amzn/awsui-components-react/polaris/header';
import { ColumnDefinitions, PaginationLabels } from './table-config';
import { useBundle } from '@amzn/react-arb-tools';
import { useQuery } from '@tanstack/react-query'
import {
  isFromDateValid,
  isThroughDateValid,
  queryAccessLevelApproverGroups,
  queryAccessLevelsForSite,
  queryLookupTypeValueForTypeAndDescription,
  querySites
} from 'src/components/utils';
import { TVisitor } from 'src/types';
import { LookupTypes } from 'src/constants/Constants';
import { debug } from '../../../../utils/commonUtils';

export interface IVendorAccessDetailsRecord {
  id: number;
  accessLevelId: string | undefined;
  accessLevelName: string | undefined;
  site: string | undefined;
  validFrom: string | undefined;
  validThrough: string | undefined;
}

export interface IVendorAccessDetailsTablePanelProps{
  accessLevels: IVendorAccessDetailsRecord[];
  vendors: TVisitor[];
  clear: boolean;
  detailsClearedCallback: Function;
  setAccessLevelsCallback: Function;
  username: string;
  visitorId: string | undefined;
}

const defaultValidFromTime = '08:00';
const defaultValidThroughTime = '17:00';

export default function VendorAccessDetailsTablePanel(props: IVendorAccessDetailsTablePanelProps) {
  debug(`AccessDetailsTablePanel() props is ${JSON.stringify(props)}`);

  const [accessLevelInput, setAccessLevelInput] = useState<SelectProps.Option | null>(null);
  const [accessLevelOptions, setAccessLevelOptions] = useState<SelectProps.Options>([]);
  const [adding, setAdding] = useState<boolean>(false);
  const [allItems, setAllItems] = useState<IVendorAccessDetailsRecord[]>(props.accessLevels);
  const [checkAccessLevelApproverGroups, setCheckAccessLevelApproverGroups] = useState<boolean>(false);
  const [duplicateInputAccessLevelFound, setDuplicateInputAccessLevelFound] = useState<boolean>(false);
  const [hoursExtFromDate, setHoursExtFromDate] = React.useState<number>(0);
  const [id, setId] = useState<number>(1);
  const [invalidFromDate, setInvalidFromDate] = useState<boolean>(false);
  const [invalidThroughDate, setInvalidThroughDate] = useState<boolean>(false);
  const [hideTable, setHideTable] = useState<boolean>(false);
  const [siteInput, setSiteInput] = useState<SelectProps.Option | null>(null);
  const [selectedItems, setSelectedItems] = useState<IVendorAccessDetailsRecord[]>([]);
  const [validFromDateInput, setValidFromDateInput] = useState<string>('');
  const [validFromTimeInput, setValidFromTimeInput] = useState<string>(defaultValidFromTime);
  const [validThroughDateInput, setValidThroughDateInput] = useState<string>('');
  const [validThroughTimeInput, setValidThroughTimeInput] = useState<string>(defaultValidThroughTime);
  const [vendorDayPassMaxDays, setVendorDayPassMaxDays] = React.useState<number>(7);

  const [bundle, isBundleLoading] = useBundle('components.SelfService.RequestVendorAccess.AccessDetails.TablePanel');

  const siteRef = useRef<HTMLInputElement>(null);

  const pageSize = 10;

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    allItems,
    {
      pagination: { pageSize: pageSize },
      sorting: {},
      selection: { trackBy: 'id' }
    }
  );

  const itemsCount = (): number => {
    if (allItems) return allItems.length;
    return 0;
  };

  const duplicateInputAccessLevel = (): boolean => {
    debug(`duplicateInputAccessLevel()`);
    const foundItem = allItems.find(i => {
      debug(`duplicateInputAccessLevel() ${i.accessLevelId} == ${accessLevelInput?.value}`);
      const result = i.accessLevelId == accessLevelInput?.value;
      debug(`duplicateInputAccessLevel() result is ${result}`);
      return result;
    });
    debug(`duplicateInputAccessLevel() foundItem is ${JSON.stringify(foundItem)}`);
    if (foundItem) return true;
    return false;
  };

  const addHandler = () => {
    setAdding(true);
    let i = id + 1;
    setId(i);
    const newItems = [];
    let invalidDate = false;
    if (!isFromDateValid(new Date(`${validFromDateInput} ${validFromTimeInput}`), hoursExtFromDate)) {
      setInvalidFromDate(true);
      invalidDate = true;
    }
    if (!isThroughDateValid(
        new Date(`${validFromDateInput} ${validFromTimeInput}`),
        new Date(`${validThroughDateInput} ${validThroughTimeInput}`),
        vendorDayPassMaxDays))
    {
      setInvalidThroughDate(true);
      invalidDate = true;
    }
    if (invalidDate) {
      setAdding(false);
      return;
    }
    if (!duplicateInputAccessLevel())
      newItems.push(
        {
          accessLevelId: accessLevelInput?.value,
          accessLevelName: accessLevelInput?.label,
          id: i,
          site: siteInput?.value,
          validFrom: `${validFromDateInput} ${validFromTimeInput}`,
          validThrough: `${validThroughDateInput} ${validThroughTimeInput}`
        }
      );
    else
      setDuplicateInputAccessLevelFound(true);
    props.setAccessLevelsCallback([...allItems, ...newItems]);
    setAdding(false);
  };

  const fromDateInputChangeHandler = (value: string) => {
    setValidFromDateInput(value);
    setInvalidFromDate(!isFromDateValid(new Date(`${value} ${validFromTimeInput}`), hoursExtFromDate));
    setInvalidThroughDate(
      validThroughDateInput == ''
        ? false 
        : !isThroughDateValid(
          new Date(`${value} ${validFromTimeInput}`),
          new Date(`${validThroughDateInput} ${validThroughTimeInput}`)));
  };

  const fromTimeInputChangeHandler = (value: string) => {
    setValidFromTimeInput(value);
    setInvalidFromDate(!isFromDateValid(new Date(`${validFromDateInput} ${value}`), hoursExtFromDate));
    setInvalidThroughDate(
      validThroughDateInput == ''
        ? false 
        : !isThroughDateValid(
          new Date(`${validFromDateInput} ${value}`),
          new Date(`${validThroughDateInput} ${validThroughTimeInput}`)));
  };

  const throughDateInputChangeHandler = (value: string) => {
    setValidThroughDateInput(value)
    setInvalidThroughDate(
      validThroughDateInput == ''
        ? false 
        : !isThroughDateValid(
          new Date(`${validFromDateInput} ${validFromTimeInput}`),
          new Date(`${value} ${validThroughTimeInput}`)));
  };

  const throughTimeInputChangeHandler = (value: string) => {
    setValidThroughTimeInput(value)
    setInvalidThroughDate(
      validThroughDateInput == ''
        ? false 
        : !isThroughDateValid(
          new Date(`${validFromDateInput} ${validFromTimeInput}`),
          new Date(`${validThroughDateInput} ${value}`)));
  };

  const removeSelected = () => {
    const currentItems = allItems;
    const newItems = currentItems.filter(al => !selectedItems.find(i => i.id == al.id));
    setSelectedItems([]);
    setAllItems(newItems);
    props.setAccessLevelsCallback(newItems);
  };

  const onSelectionChangeHandler = (detail: { selectedItems: React.SetStateAction<any[]> }) => {
    setSelectedItems(detail.selectedItems as IVendorAccessDetailsRecord[]);
  };

  if (props.vendors.length > 0 && !siteInput && props.accessLevels.length == 0) siteRef.current?.focus();

  const sitesQuery = useQuery({
    queryKey: ['sites'],
    queryFn: () => querySites(true),
    retry: 3
  });

  const accessLevelsForSiteQuery = useQuery({
    queryKey: ['accessLevelsForSiteRequestVendorAccess'],
    queryFn: () => queryAccessLevelsForSite(siteInput?.value),
    onSuccess: async (data) => {
      if (!accessLevelsForSiteQuery.data) return [];
      setCheckAccessLevelApproverGroups(true);
      const options = await Promise.all(data.map(async (al): Promise<any> => {
        debug(`accessLevelsForSiteQuery() onSuccess() al is ${JSON.stringify(al)}`);
        try {
          const accessLevelApproverGroups = await queryAccessLevelApproverGroups(al.AccessLevelName);
          debug(`accessLevelsForSiteQuery() onSuccess() accessLevelsApproverGroups is ${JSON.stringify(accessLevelApproverGroups)}`);
          return {
            label: al.AccessLevelName,
            value: al.AccessLevelID?.toString(),
            disabled:
              !accessLevelApproverGroups
              || accessLevelApproverGroups.length == 0
              || al.AccessLevelName.includes('-4-')
              || al.AccessLevelName.includes('-5-')
          };
        } catch(error) {
          return {
            label: al.AccessLevelName,
            value: al.AccessLevelID?.toString(),
            disabled: true
          };
        }
      }));
      debug(`accessLevelsForSiteQuery() onSuccess() options is ${JSON.stringify(options)}`);
      setAccessLevelOptions(options);
      setCheckAccessLevelApproverGroups(false);
    },
    retry: 3
  });

  const accessLevels = () => {
    debug(`AccessDetailsTablePanel() accessLevelOptions is ${JSON.stringify(accessLevelOptions)}`);
    const values = [];
    const available = {
      label: bundle.getMessage('available-access-levels'),
      options: accessLevelOptions.filter(al => !al.disabled),
    };
    const unavailableContingentWorker = {
      label: bundle.getMessage('unavailable-contingent-worker'),
      options: accessLevelOptions.filter(al => al.disabled && (al.label?.includes('-4-') || al.label?.includes('-5'))),
    };
    const unavailableNoApproverGroupLinked = {
      label: bundle.getMessage('unavailable-no-approver-group-linked'),
      options: accessLevelOptions.filter(al => al.disabled && !(al.label?.includes('-4-') || al.label?.includes('-5'))),
    };
    if (available.options.length > 0) values.push(available);
    if (unavailableContingentWorker.options.length > 0) values.push(unavailableContingentWorker);
    if (unavailableNoApproverGroupLinked.options.length > 0) values.push(unavailableNoApproverGroupLinked);
    return(values);
  };

  useEffect(() => {
    setAccessLevelOptions([]);
    setAccessLevelInput(null);
    accessLevelsForSiteQuery.refetch();
  }, [siteInput])

  useEffect(() => {
    setSiteInput(null);
    setAccessLevelInput(null);
    setValidFromDateInput('');
    setValidFromTimeInput(defaultValidFromTime);
    setValidThroughDateInput('');
    setValidThroughTimeInput(defaultValidThroughTime);
    setInvalidFromDate(false);
    setInvalidThroughDate(false);
    props.detailsClearedCallback();
  }, [props.clear])

  const siteInputHandler = (detail: SelectProps.ChangeDetail) => {
    setAccessLevelInput(null);
    setSiteInput(detail.selectedOption);
  };

  const FlashMessageDuplicateAccessLevelInput = () => (
    <Flashbar
      items = {
        [
          {
            type: 'error',
            dismissible: true,
            content: bundle.getMessage('duplicate-access-level-input'),
            onDismiss: () => {setDuplicateInputAccessLevelFound(false)}
          },
        ]
      }
    />
  );

  useEffect(() => {
    setAllItems(props.accessLevels);
  }, [props.accessLevels])

  useEffect(() => {
    queryLookupTypeValueForTypeAndDescription(LookupTypes.WelcomeApplicationSettings,'Vendor Day Pass Max Days')
      .then((v) => setVendorDayPassMaxDays(parseInt(v.value)));
    queryLookupTypeValueForTypeAndDescription(LookupTypes.WelcomeApplicationSettings, 'Hours Extension - From Date Form Validation')
      .then((v) => setHoursExtFromDate(parseInt(v.value)));
  }, []);

  if (isBundleLoading) return <Spinner/>;

  return(
    <>
    {duplicateInputAccessLevelFound && <FlashMessageDuplicateAccessLevelInput/>}
    <div id='RequestVendorAccessAccessDetailsTableDiv' hidden={hideTable}>
      <Table
        {...collectionProps}
        columnDefinitions={ColumnDefinitions}
        header={
          <>
            <Header
              counter={`(${itemsCount().toString()})`}
            >
              {bundle.getMessage('access-details')}
            </Header>
            <Box float='left' margin='s'>
              <SpaceBetween direction='horizontal' size='s'>
                <FormField label={bundle.getMessage('site')} stretch={true}>
                  <div style={{width: 120}}>
                    <Select
                      data-testid='RequestVendorAccessAccessDetailsSitesSelect'
                      filteringType='auto'
                      loadingText={bundle.getMessage('loading-sites')}
                      onChange={({ detail }) => siteInputHandler(detail)}
                      options={
                        sitesQuery.data?.map(s => {
                          return {label: s.SiteCode, value: s.SiteCode};
                        })
                      }
                      placeholder={bundle.getMessage('select-a-site')}
                      ref={siteRef}
                      selectedOption={siteInput}
                      statusType={sitesQuery.isLoading || sitesQuery.isFetching? 'loading' : 'finished'}
                    />
                  </div>
                </FormField>
                <FormField label={bundle.getMessage('access-level')} stretch={true}>
                  <div style={{width: 300}}>
                    <Select
                      data-testid='RequestVendorAccessAccessDetailsAccessLevelSelect'
                      disabled={!siteInput}
                      filteringType='auto'
                      loadingText={bundle.getMessage('loading-site-access-levels')}
                      onChange={({ detail }) => setAccessLevelInput(detail.selectedOption)}
                      options={accessLevels()}
                      placeholder={bundle.getMessage('select-an-access-level')}
                      selectedOption={accessLevelInput}
                      statusType={
                        accessLevelsForSiteQuery.isLoading || accessLevelsForSiteQuery.isFetching || checkAccessLevelApproverGroups
                        ? 'loading'
                        : 'finished'}
                    />
                  </div>
                </FormField>
                <FormField label={bundle.getMessage('valid-from')} >
                  <DatePicker
                    data-testid='RequestVendorAccessAccessDetailsDateFromDatePicker'
                    onChange={event => fromDateInputChangeHandler(event.detail.value)}
                    nextMonthAriaLabel={bundle.getMessage('next-month')}
                    invalid={invalidFromDate}
                    isDateEnabled={date => {
                      const [month, day, year] = [
                        (date.getMonth()+1).toString().padStart(2,'0'),
                        date.getDate().toString().padStart(2,'0'),
                        date.getFullYear(),
                      ];
                      const convertedDate = new Date(`${year}-${month}-${day} 23:59:59`);
                      return isFromDateValid(convertedDate, hoursExtFromDate);
                    }}
                    placeholder='YYYY/MM/DD'
                    previousMonthAriaLabel={bundle.getMessage('previous-month')}
                    todayAriaLabel={bundle.getMessage('today')}
                    value={validFromDateInput}
                  />
                </FormField>
                <FormField label='&zwnj;'>
                  <TimeInput
                    data-testid='RequestVendorAccessAccessDetailsDateFromTimeInput'
                    format='hh:mm'
                    onChange={event => fromTimeInputChangeHandler(event.detail.value)}
                    invalid={invalidFromDate}
                    placeholder='hh:mm'
                    value={validFromTimeInput}
                  />
                </FormField>
                <FormField label={bundle.getMessage('valid-through')} >
                  <DatePicker
                    data-testid='RequestVendorAccessAccessDetailsDateThroughDatePicker'
                    onChange={event => throughDateInputChangeHandler(event.detail.value)}
                    invalid={invalidThroughDate}
                    isDateEnabled={date => {
                      const [month, day, year] = [
                        (date.getMonth()+1).toString().padStart(2,'0'),
                        date.getDate().toString().padStart(2,'0'),
                        date.getFullYear(),
                      ];
                      const throughDate = `${year}-${month}-${day} 23:59:49`;
                      return isThroughDateValid(new Date(`${validFromDateInput} ${validFromTimeInput}`), new Date(throughDate), vendorDayPassMaxDays);
                    }}
                    nextMonthAriaLabel={bundle.getMessage('next-month')}
                    placeholder='YYYY/MM/DD'
                    previousMonthAriaLabel={bundle.getMessage('previous-month')}
                    todayAriaLabel={bundle.getMessage('today')}
                    value={validThroughDateInput}
                  />
                </FormField>
                <FormField label='&zwnj;'>
                  <TimeInput
                    data-testid='RequestVendorAccessAccessDetailsDateThroughTimeInput'
                    format='hh:mm'
                    invalid={invalidThroughDate}
                    onChange={event => throughTimeInputChangeHandler(event.detail.value)}
                    placeholder='hh:mm'
                    value={validThroughTimeInput}
                  />
                </FormField>
                <div style={{position:'relative', bottom: '-25px'}}>
                  <Button
                    data-testid='RequestVendorAccessAccessDetailsAddButton'
                    disabled={
                      !siteInput
                      || !accessLevelInput
                      || !validFromDateInput || !validFromTimeInput || !validThroughDateInput || !validThroughTimeInput || invalidFromDate || invalidThroughDate
                    }
                    loading={adding}
                    onClick={addHandler}
                    variant='normal'
                  >
                    {bundle.getMessage('add')}
                  </Button>
                </div>
              </SpaceBetween>
            </Box>
          </>
        }
        footer={
          <>
          <SpaceBetween direction='vertical' size='xs'>
            <Box float='right'>
              <Button
                disabled={selectedItems.length == 0}
                onClick={removeSelected}
                variant='primary'
              >
                {bundle.getMessage('remove-selected')}
              </Button>
            </Box>
          </SpaceBetween>
          </>
        }
        items={items}
        selectionType='multi'
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) =>
          onSelectionChangeHandler(detail)
        }
        pagination={
          allItems.length > pageSize
          &&
          <Pagination
            {...paginationProps}
            ariaLabels={PaginationLabels}
          />
        }
        resizableColumns={true}
        trackBy='id'
      />
    </div>
    </>
  );
}
